// src/lib/supabaseClient.js
// Central Supabase client — proxied securely to hide API keys
import { createClient } from "@supabase/supabase-js";

// Read keys directly from .env (Anon key is designed to be public and secured by RLS)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    "[Supabase] Missing env vars: REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_PUBLISHABLE_KEY. " +
    "Make sure your .env file is set up correctly and the dev server is restarted."
  );
}

export const supabase = createClient(
  supabaseUrl || "https://placeholder.supabase.co",
  supabaseAnonKey || "placeholder-key"
);


// ─── Edge Function base URLs ──────────────────────────────────────────────────
// All complex operations go through Edge Functions using the user's JWT.
// Simple reads could use PostgREST directly via supabase.from('table').select(),
// but for consistency and RBAC enforcement we route everything through Edge Functions.

export const EDGE = {
  workflow: `${supabaseUrl}/functions/v1/workflow`,
  analytics: `${supabaseUrl}/functions/v1/analytics`,
  quotation: `${supabaseUrl}/functions/v1/quotation`,
  admin: `${supabaseUrl}/functions/v1/admin`,
};

// ─── Authenticated fetch helper ───────────────────────────────────────────────
// Automatically attaches the current user's JWT to every Edge Function call.
export async function edgeFetch(url, body) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token || localStorage.getItem("token");

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = await res.json();
  if (!res.ok) {
    // Only force-logout on genuine JWT expiry/invalidity (401 with JWT-specific message).
    // Normal RBAC 403s or validation 400s must NOT clear the session —
    // they should be handled by the calling component.
    if (res.status === 401) {
      const msg = (data.message || "").toLowerCase();
      const isJwtError =
        msg.includes("invalid jwt") ||
        msg.includes("jwt expired") ||
        msg.includes("not authenticated") ||
        msg.includes("token is expired");
      if (isJwtError) {
        console.warn(
          "[edgeFetch] JWT expired — clearing session and redirecting to login.",
        );
        supabase.auth.signOut().catch(() => {});
        localStorage.clear();
        window.location.href = "/login";
        return; // prevent throw after redirect
      }
    }
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}
